// ============================================================================
// lib/site/siteListParams.ts — СОСТОЯНИЕ СПИСКА ЗАПИСЕЙ САЙТА В URL (SSoT/SRP):
// страница, поиск, сортировка и отборы блока `site_list` живут в query-строке
// (ссылка/назад/перезагрузка сохраняют вид списка). Здесь — только разбор и
// сборка (без React/SDK): блок и панель фильтров используют одни функции.
// Отборы уходят на СЕРВЕР (SiteService.ListSiteRecords.filters) — клиент не
// фильтрует данные сам.
// ============================================================================

/** SiteListFilter — отбор (совместим с FilterListEditor/FilterRow). */
export interface SiteListFilter {
  field: string;
  condition?: string;
  value?: string;
}

/** SITE_LIST_RESERVED — служебные query-параметры списка (не отборы). */
export const SITE_LIST_RESERVED = new Set(['page', 'q', 'sort', 'dir', 'preview', 'preview_token', 'locale']);

/** siteListCsv — CSV-свойство блока → массив кодов (trim, без пустых). */
export function siteListCsv(value: unknown): string[] {
  return String(value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** normalizeCondition — условие FilterListEditor → условие сервера. */
export function normalizeCondition(condition: unknown): string {
  const c = String(condition ?? '').trim().toLowerCase();
  if (!c) return '=';
  if (c === 'is_null') return 'is_null';
  if (c === 'is_not_null') return 'is_not_null';
  return c;
}

/** parseStaticFilters — статические отборы блока (массив или JSON-строка). */
export function parseStaticFilters(value: unknown): SiteListFilter[] {
  let raw: unknown = value;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return [];
    try {
      raw = JSON.parse(s);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  const out: SiteListFilter[] = [];
  for (const row of raw as SiteListFilter[]) {
    const field = String(row?.field ?? '').trim();
    if (!field) continue;
    out.push({ field, condition: normalizeCondition(row?.condition), value: String(row?.value ?? '') });
  }
  return out;
}

/** SiteListState — состояние списка из query-строки. */
export interface SiteListState {
  /** Страница 1-based (в URL и интерфейсе; сервер ждёт 0-based). */
  page: number;
  q: string;
  sort: string;
  sortDesc: boolean;
  /** Значения отборов по полям (только из whitelist filterFields). */
  fieldValues: Record<string, string>;
}

/** readSiteListState — разбор query-строки: неизвестные параметры игнорируются. */
export function readSiteListState(search: URLSearchParams, filterFields: string[]): SiteListState {
  const pageRaw = parseInt(search.get('page') || '1', 10);
  const fieldValues: Record<string, string> = {};
  for (const field of filterFields) {
    const v = search.get(field);
    if (v != null && v !== '') fieldValues[field] = v;
  }
  return {
    page: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1,
    q: (search.get('q') || '').trim(),
    sort: (search.get('sort') || '').trim(),
    sortDesc: (search.get('dir') || '') === 'desc',
    fieldValues,
  };
}

/** buildSiteRecordFilters — отборы для ListSiteRecords: статические + из URL +
 *  поиск (LIKE). Пустые значения НЕ отправляются (сервер отвергает пустые). */
export function buildSiteRecordFilters(
  staticFilters: SiteListFilter[],
  state: SiteListState,
  searchField: string,
): SiteListFilter[] {
  const out: SiteListFilter[] = [];
  for (const f of staticFilters) {
    const cond = normalizeCondition(f.condition);
    const value = String(f.value ?? '').trim();
    if (cond === 'is_null' || cond === 'is_not_null') {
      out.push({ field: f.field, condition: cond, value: '' });
      continue;
    }
    if (value !== '') out.push({ field: f.field, condition: cond, value });
  }
  for (const [field, value] of Object.entries(state.fieldValues)) {
    if (value !== '') out.push({ field, condition: '=', value });
  }
  const q = state.q.trim();
  if (q !== '' && searchField) out.push({ field: searchField, condition: 'like', value: q });
  return out;
}

/** siteListParamsEqual — сравнение отборов (стабильный ключ перезагрузки). */
export function siteListParamsKey(filters: SiteListFilter[]): string {
  return filters.map((f) => `${f.field}:${normalizeCondition(f.condition)}:${f.value ?? ''}`).join('|');
}
